import React, { useEffect, useMemo } from "react";
import { Tag, Tooltip } from "antd";
import moment from "moment";

export interface TemplateStructureComponent {
  type: "HEADER" | "BODY" | "FOOTER" | "BUTTONS" | "CAROUSEL" | string;
  text?: string;
  format?: string;
  buttons?: { text: string; type: string; url?: string }[];
  cards?: {
    components: TemplateStructureComponent[];
  }[];
  example?: {
    body_text?: string[][];
  };
}

export interface TemplateStructure {
  variables?: Record<string, string>;
  components?: TemplateStructureComponent[];
  providerId?: string;
}

export interface TemplateData {
  id: number | string;
  name: string;
  category?: string;
  language?: string;
  status: string;
  is_active?: number;
  is_manual_entry?: boolean;
  template_hash?: string;
  structure?: TemplateStructure;
  last_synced_at?: string;
  created_at: string;
  updated_at: string;
  campaign_name?: string;
}

interface TemplateDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  template?: TemplateData | null;
}

const getTemplateStatusColor = (status: string) => {
  const normalized = status.toLowerCase();
  switch (normalized) {
    case "approved":
      return "success";
    case "rejected":
      return "error";
    case "pending":
      return "warning";
    default:
      return "default";
  }
};

const getCategoryLabelColor = (category?: string) => {
  const normalized = category?.toLowerCase();
  switch (normalized) {
    case "utility":
      return "#0ea5e9";
    case "marketing":
      return "#6366f1";
    case "authentication":
      return "#22c55e";
    default:
      return "#6b7280";
  }
};

const TemplateDetailsModal: React.FC<TemplateDetailsModalProps> = ({
  isOpen,
  onClose,
  template,
}) => {
  const normalizedStatus = template?.status?.toLowerCase() || "";
  const headerComponent = useMemo(
    () =>
      template?.structure?.components?.find((c) => c.type === "HEADER") || null,
    [template]
  );
  const bodyComponent = useMemo(
    () =>
      template?.structure?.components?.find((c) => c.type === "BODY") || null,
    [template]
  );
  const footerComponent = useMemo(
    () =>
      template?.structure?.components?.find((c) => c.type === "FOOTER") || null,
    [template]
  );
  const buttonsComponent = useMemo(
    () =>
      template?.structure?.components?.find((c) => c.type === "BUTTONS") ||
      null,
    [template]
  );

  const variables = template?.structure?.variables || {};
  const components = template?.structure?.components || [];

  // Disable background scroll while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen || !template) return null;

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9000,
        padding: 24,
        boxSizing: "border-box",
      }}
      // disable background click
      onClick={() => {}}>
      <div
        className="shadow-lg"
        style={{
          background: "#ffffff",
          borderRadius: 18,
          width: "100%",
          maxWidth: 900,
          height: "90vh",
          overflow: "hidden",
          boxShadow:
            "0 24px 60px rgba(15,23,42,0.25), 0 0 0 1px rgba(148,163,184,0.18)",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div
          className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom"
          style={{
            background: "#f9fafb",
            borderColor: "#e5e7eb",
          }}>
          <div>
            <h5 className="mb-0 fw-semibold">
              {template.name}{" "}
              <span className="text-muted fw-normal" style={{ fontSize: 13 }}>
                · {template.language || "N/A"}
              </span>
            </h5>
            <div className="d-flex align-items-center gap-2 mt-1">
              <Tag
                color={getTemplateStatusColor(normalizedStatus)}
                style={{ marginBottom: 0 }}>
                {template.status}
              </Tag>
              {template.category && (
                <span
                  style={{
                    fontSize: 12,
                    padding: "2px 8px",
                    borderRadius: 999,
                    border: "1px solid rgba(148,163,184,0.7)",
                    color: getCategoryLabelColor(template.category),
                    background: "#ffffff",
                  }}>
                  {template.category}
                </span>
              )}
              {template.structure?.providerId && (
                <span className="text-muted" style={{ fontSize: 11 }}>
                  Provider ID: {template.structure.providerId}
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              height: 32,
              width: 32,
              borderRadius: "999px",
              fontSize: 18,
              lineHeight: 1,
              cursor: "pointer",
              color: "#6b7280",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: "18px 24px 18px",
            overflowY: "auto",
            flex: 1,
            background: "#f3f4f6",
          }}>
          <div className="row" style={{ margin: 0, rowGap: 16 }}>
            {/* Left: Meta + Variables */}
            <div className="col-md-5 mb-3">
              {/* Meta */}
              <div
                className="mb-3 p-3 rounded-3"
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                }}>
                <div className="d-flex justify-content-between mb-2">
                  <span
                    className="text-uppercase text-muted"
                    style={{ fontSize: 11, letterSpacing: "0.08em" }}>
                    Meta
                  </span>
                  {template.template_hash && (
                    <Tooltip title={template.template_hash}>
                      <span className="text-muted" style={{ fontSize: 11 }}>
                        Hash: {template.template_hash.slice(0, 10)}…
                      </span>
                    </Tooltip>
                  )}
                </div>

                <div className="small text-muted" style={{ fontSize: 12 }}>
                  <div className="d-flex justify-content-between mb-1">
                    <span>Template ID</span>
                    <span className="text-dark fw-semibold">{template.id}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>Category</span>
                    <span>{template.category || "-"}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>Language</span>
                    <span>{template.language || "-"}</span>
                  </div>
                  {template.last_synced_at && (
                    <div className="d-flex justify-content-between mb-1">
                      <span>Last synced</span>
                      <span>
                        {moment(template.last_synced_at).format(
                          "YYYY-MM-DD HH:mm"
                        )}
                      </span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between mb-1">
                    <span>Created</span>
                    <span>
                      {moment(template.created_at).format("YYYY-MM-DD HH:mm")}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Updated</span>
                    <span>
                      {moment(template.updated_at).format("YYYY-MM-DD HH:mm")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Variables */}
              <div
                className="p-3 rounded-3"
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  minHeight: 100,
                }}>
                <div className="d-flex justify-content-between mb-2">
                  <span
                    className="text-uppercase text-muted"
                    style={{ fontSize: 11, letterSpacing: "0.08em" }}>
                    Variables
                  </span>
                  <span
                    className="badge bg-light text-dark"
                    style={{ fontSize: 10 }}>
                    {Object.keys(variables).length} placeholders
                  </span>
                </div>
                {Object.keys(variables).length === 0 ? (
                  <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                    This template does not use any dynamic variables.
                  </p>
                ) : (
                  <div
                    style={{
                      maxHeight: 160,
                      overflowY: "auto",
                      paddingRight: 4,
                    }}>
                    {Object.entries(variables).map(([key, value]) => (
                      <div
                        key={key}
                        className="d-flex justify-content-between align-items-center mb-1">
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: 11,
                            color: "#6b7280",
                          }}>
                          {key}
                        </span>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: 12,
                            color: "#111827",
                          }}>
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Preview + components */}
            <div className="col-md-7 mb-3">
              <div
                className="p-3 rounded-3"
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  minHeight: 260,
                }}>
                <div className="d-flex justify-content-between mb-2">
                  <span
                    className="text-uppercase text-muted"
                    style={{ fontSize: 11, letterSpacing: "0.08em" }}>
                    Preview
                  </span>
                </div>

                <div
                  style={{
                    borderRadius: 16,
                    background: "#f9fafb",
                    padding: 12,
                    border: "1px solid #e5e7eb",
                    color: "#111827",
                    fontSize: 13,
                  }}>
                  {/* Header */}
                  {headerComponent && (
                    <div className="mb-2">
                      <span
                        style={{
                          fontSize: 12,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          color: "#1d4ed8",
                        }}>
                        {headerComponent.text || "HEADER"}
                      </span>
                    </div>
                  )}

                  {/* Body */}
                  {bodyComponent && (
                    <div className="mb-2" style={{ whiteSpace: "pre-wrap" }}>
                      {bodyComponent.text}
                    </div>
                  )}

                  {/* Footer */}
                  {footerComponent && (
                    <div
                      className="mt-3 pt-2 text-muted"
                      style={{
                        fontSize: 11,
                        borderTop: "1px dashed #e5e7eb",
                      }}>
                      {footerComponent.text}
                    </div>
                  )}

                  {/* Buttons */}
                  {buttonsComponent?.buttons &&
                    buttonsComponent.buttons.length > 0 && (
                      <div className="mt-3 d-flex flex-wrap gap-2">
                        {buttonsComponent.buttons.map((btn, idx) => (
                          <button
                            key={idx}
                            type="button"
                            disabled
                            style={{
                              borderRadius: 999,
                              border: "1px solid #2563eb",
                              padding: "4px 10px",
                              background: "#eff6ff",
                              color: "#1d4ed8",
                              fontSize: 11,
                              cursor: "default",
                            }}>
                            {btn.text}
                          </button>
                        ))}
                      </div>
                    )}
                </div>
              </div>

              {/* Components summary */}
              {components.length > 0 && (
                <div
                  className="mt-3 p-3 rounded-3"
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                  }}>
                  <div className="d-flex justify-content-between mb-2">
                    <span
                      className="text-uppercase text-muted fw-medium"
                      style={{ fontSize: 11, letterSpacing: "0.08em" }}>
                      Components
                    </span>
                    <span
                      className="badge bg-light text-dark"
                      style={{ fontSize: 10 }}>
                      {components.length} blocks
                    </span>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    {components.map((c, idx) => (
                      <span
                        key={`${c.type}-${idx}`}
                        className="badge  badge-sm badge-success "
                        style={{ fontSize: 11 }}>
                        {c.type}
                        {c.format ? ` · ${c.format}` : ""}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="d-flex justify-content-end px-4 py-3 border-top"
          style={{ background: "#ffffff", borderColor: "#e5e7eb" }}>
          <button
            type="button"
            className="btn btn-light me-2"
            onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateDetailsModal;
